import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Context, { IContext } from '../context';
import { useGetCurrentLoggedInUserMutation } from '../../graphql/mutation/user/getCurrentLoggedInUserMutation';
import { IUserSchema } from '../../graphql/type/user';
import { COOKIE_KEY } from '../components/constant/cookie';
import useCookie from '../components/_hook/useCookie';
import useEnvironment from '../components/_hook/useEnvironment';
import { useCurrencyQuery } from '../../graphql/query/currency/useCurrencyQuery';
import { ICurrencySchema } from '../../graphql/type/currency';
import { useUserUpdatedSubscription } from '../../graphql/subscription/user/userUpdatedSubscription';
import { useCompanyLazyQuery } from '../../graphql/query/company/useCompanyQuery';
import useNotification from '../components/_hook/useNotification';
import { useKeepitalSubscriptionPackageQuery } from '../../graphql/query/keepitalSubscriptionPackage/useKeepitalSubscriptionPackageQuery';
import { IKeepitalSubscriptionPackageSchema } from '../../graphql/type/keepitalSubscriptionPackage';
import KEEPITAL_SUBSCRIPTION_PACKAGE from '../components/constant/keepitalSubscriptionPackage';
import { usePlanUpdatedSubscription } from '../../graphql/subscription/plan/planUpdatedSubscription';
import ContextPlanUpdatedSubscription from './contextSubscriptions/ContextPlanUpdatedSubscription';

interface IProps {
  children: React.ReactNode;
}

const ContextCompanySubscriptionPackageDataQueryFragment = `
        _id
        name {
          text
          la
        }
        status
        type
        apps {
          account {
            status
            limitation
          }
          messenger {
            status
            
            limitation
          }
          announce {
            status
            
            limitation
          }
          procurement {
            status
            
            limitation
          }
          business {
            status
            
            limitation
          }
          product {
            status
            
            limitation
          }
          minisite {
            status
            
            limitation
          }
          code {
            status
            
            limitation
          }
        }
`;

export const ContextCompanyQueryFragment = `
  _id
  verification
  name {
      la
      text
  }
  origin
  slug
  media {
      images {
          logo {
            status
            filename
            filename_small

            filenameUrl
            filenameSmallUrl
          }
      }
  }
  settings {
    enableDropshipping
  }
  planData {
    _id
    status
    subscription {
      packageID
      packageData {
        ${ContextCompanySubscriptionPackageDataQueryFragment}
      }
    }
  }
`;

export default function ContextWrapper(props: IProps) {
  const [user, setUser] = useState<IContext['user']>(
    (null as unknown) as IContext['user']
  );
  const [company, setCompany] = useState<IContext['company']>(
    (null as unknown) as IContext['company']
  );
  const [isSuperAdmin, setIsSuperAdmin] = useState<IContext['isSuperAdmin']>(
    false
  );
  const { t } = useTranslation();
  const { children } = props;
  const { setCookie } = useCookie([]);
  const environment = useEnvironment();
  const { notification } = useNotification();
  const { i18n } = useTranslation();

  const [getCurrentLoggedInUser] = useGetCurrentLoggedInUserMutation<{
    currentLoggedInUser: IUserSchema;
    isSuperAdmin: boolean;
  }>(
    `
    currentLoggedInUser {
      _id
      companies {
        id
        companyData {
          ${ContextCompanyQueryFragment}
        }
      }
      profile {
        name {
          title
          firstname
          lastname
        }
        media {
          images {
            avatar {
              status
              filename
              filename_small
              filename_medium

              filenameUrl
              filenameSmallUrl
              filenameMediumUrl
            }
          }
        }

        settings {
          company
          language
          country
          currency
        }
      }

    }
    isSuperAdmin
  `,
    {
      onCompleted: (data) => {
        if (
          data &&
          data.getCurrentLoggedInUser &&
          data.getCurrentLoggedInUser.currentLoggedInUser
        ) {
          const { currentLoggedInUser } = data.getCurrentLoggedInUser;
          setUser(currentLoggedInUser);
          setIsSuperAdmin(data.getCurrentLoggedInUser.isSuperAdmin);
          if (
            !currentLoggedInUser ||
            !currentLoggedInUser.companies ||
            !currentLoggedInUser.companies.length
          ) {
            window.location.href = environment.REACT_APP_KEEPITAL_URL;
          }
        } else {
          window.location.href = environment.REACT_APP_KEEPITAL_URL;
        }
      },
      onError: () => {
        window.location.href = environment.REACT_APP_KEEPITAL_URL;
      },
    }
  );

  const {
    data: keepitalSubscriptionPackageData,
  } = useKeepitalSubscriptionPackageQuery<IKeepitalSubscriptionPackageSchema>(
    ContextCompanySubscriptionPackageDataQueryFragment,
    {
      variables: {
        filter: {
          type: KEEPITAL_SUBSCRIPTION_PACKAGE.TYPE.FREE,
        },
        limit: 1,
      },
    }
  );

  const keepitalSubscriptionFreePackage =
    keepitalSubscriptionPackageData &&
    keepitalSubscriptionPackageData.keepitalSubscriptionPackages &&
    keepitalSubscriptionPackageData.keepitalSubscriptionPackages.items &&
    keepitalSubscriptionPackageData.keepitalSubscriptionPackages.items[0]
      ? keepitalSubscriptionPackageData.keepitalSubscriptionPackages.items[0]
      : null;

  const { data: currenciesData } = useCurrencyQuery<ICurrencySchema>(
    `
      _id
      primary
      status
      name {
            text
            la
          }
      base
      iso3
      numeric_code
      rate
      symbol
      decimal_place
      decimal_symbol
      format_positive
      format_negative
      thousands_symbol
  `
  );

  const currencies = currenciesData ? currenciesData.currencies.items : [];

  useEffect(() => {
    getCurrentLoggedInUser();
  }, []);

  const [getCompanies, { loading, data }] = useCompanyLazyQuery(
    ContextCompanyQueryFragment
  );

  function refetchUserAndCompanyInfo() {
    if (company) {
      getCompanies({
        variables: {
          filter: {
            _id: company._id,
          },
        },
      });
    }
    getCurrentLoggedInUser();
  }

  useUserUpdatedSubscription(`updatedUserId`, {
    variables: { userId: user ? user._id : '' },
    shouldResubscribe: true,
    onSubscriptionData: ({ subscriptionData }) => {
      if (
        subscriptionData &&
        subscriptionData.data &&
        subscriptionData.data.userUpdated &&
        subscriptionData.data.userUpdated.updatedUserId
      ) {
        getCurrentLoggedInUser();
      }
    },
  });

  useEffect(() => {
    if (
      user &&
      user.profile &&
      user.profile.settings &&
      user.profile.settings.country
    ) {
      setCookie(COOKIE_KEY.COUNTRY, user.profile.settings.country);
    }
  }, [
    user &&
    user.profile &&
    user.profile.settings &&
    user.profile.settings.country
      ? user.profile.settings.country
      : false,
  ]);

  useEffect(() => {
    if (
      user &&
      user.profile &&
      user.profile.settings &&
      user.profile.settings.currency
    ) {
      setCookie(COOKIE_KEY.CURRENCY, user.profile.settings.currency);
    }
  }, [
    user &&
    user.profile &&
    user.profile.settings &&
    user.profile.settings.currency
      ? user.profile.settings.currency
      : false,
  ]);

  useEffect(() => {
    if (
      user &&
      user.profile &&
      user.profile.settings &&
      user.profile.settings.language
    ) {
      setCookie(COOKIE_KEY.LANGUAGE, user.profile.settings.language);
      // @ts-ignore
      i18n.changeLanguage(user.profile.settings.language);
    }
  }, [
    user &&
    user.profile &&
    user.profile.settings &&
    user.profile.settings.language
      ? user.profile.settings.language
      : false,
  ]);

  useEffect(() => {
    if (!loading && data && data.companies.items.length) {
      setCompany(data.companies.items[0]);
    }
  }, [loading]);

  useEffect(() => {
    if (company) {
      if (user && user.companies && user.companies.length) {
        const tempCompany = user.companies.find((i) => i.id === company._id);
        if (!tempCompany && !isSuperAdmin) {
          notification.warning(
            t('you_are_not_allow_to_access_restricted_area')
          );
          const tempCompanyData = user.companies.find((i) => i.companyData);

          if (tempCompanyData && tempCompanyData.companyData) {
            setCompany(tempCompanyData.companyData);
          }
        }
      }
    }
  }, [JSON.stringify(company)]);

  const memoContext = useMemo(
    () => ({
      currencies,
      user,
      isSuperAdmin,
      company,
      setCompany: (companyId: string) => {
        if (user && user.companies) {
          const tempCompany = user.companies.find((i) => i.id === companyId);
          if (tempCompany && tempCompany.companyData) {
            setCompany(tempCompany.companyData);
          } else {
            getCompanies({
              variables: {
                filter: {
                  _id: companyId,
                },
              },
            });
          }
        }
      },
      companySubscriptionPackage:
        company &&
        company.planData &&
        company.planData.subscription &&
        company.planData.subscription.packageData
          ? company.planData.subscription.packageData
          : keepitalSubscriptionFreePackage,
      refetchUserAndCompanyInfo,
    }),
    [
      JSON.stringify(user),
      JSON.stringify(currencies),
      JSON.stringify(company),
      isSuperAdmin,
    ]
  );

  if (!user) {
    return null;
  }

  return (
    <Context.Provider value={memoContext}>
      <ContextPlanUpdatedSubscription />
      {children}
    </Context.Provider>
  );
}
